import { get } from 'lodash';
import { REHYDRATE } from 'redux-persist';

import {
  SYNC_ACCESS_TOKEN,
  // SYNC_TRANSACTION_TAG,
} from 'Common/Constants';
import getDatabase, {
  closeDb,
  DB_TABLE_NAMES_MAP,
  generateDatabaseName,
  generateIdentifier,
} from 'Common/DB';
import { extractPreferencesFromAccessInfo } from 'Common/Security/SecurityHelpers';
import { token } from 'API/MPWApi';
import * as SecurityTypes from 'Common/Security/state/types';
import * as POSTypes from 'PointOfSale/state/types';
import * as SetDateAndSiteTypes from 'PointOfSale/modules/setDateAndSite/state/types';
import * as ConnectionTypes from 'State/connection/types';
import * as ConnectionSelectors from 'State/connection/selectors';
import { writeLocalError } from 'PointOfSale/state/actions';
import { getDatabaseName } from 'State/selectors';
import { APP_START_UP } from 'State/app/types';
import { TransactionEnum } from 'Common/FieldEnum';
import { syncOfflineTransactionHistory } from 'Offline/actions';

import { getOfflineTransactionType } from './helpers';
// import registerSync from './registerSync';
/**
 * 1. Captures actions and stores data locally
 * 2. Triggers syncOfflineTransactionHistory when going back online or when the server was down and is restored
 * 3. Removes locally stored transaction on successful HTTP POST /Serving/Transaction
 */

export default () => {
  return ({ dispatch, getState }) => (next) => async (action) => {
    switch (action.type) {
      case APP_START_UP: {
        return next(action);
      }
      case REHYDRATE: {
        next(action);
        try {
          if (!token.isValid()) {
            break;
          }

          const userInfo = get(action, 'payload.SecurityState.userInfo', {});
          const { districtId, userName } = userInfo;
          const identifier = generateIdentifier(districtId, userName);
          const databaseName = generateDatabaseName(identifier);

          const DB = await getDatabase(databaseName);
          const accessInfo = get(
            action,
            'payload.SecurityState.accessInfo',
            []
          );
          const preferences = get(
            action,
            'payload.SecurityState.preferences',
            []
          );
          await DB.AccessToken.update(SYNC_ACCESS_TOKEN, {
            userInfo,
            accessInfo,
            preferences,
          });
        } catch (error) {
          dispatch(writeLocalError(DB_TABLE_NAMES_MAP.AccessToken, error));
        }
        break;
      }
      case SecurityTypes.SECURITY_SET_USER_INFO: {
        next(action);
        try {
          const { userInfo, hash } = action.payload;

          const { districtId, userName } = userInfo;
          const identifier = generateIdentifier(districtId, userName);
          const databaseName = generateDatabaseName(identifier);

          const DB = await getDatabase(databaseName);
          await DB.AccessToken.put({
            Id: SYNC_ACCESS_TOKEN,
            userInfo,
            hash,
          });
        } catch (error) {
          dispatch(writeLocalError(DB_TABLE_NAMES_MAP.AccessToken, error));
        }
        break;
      }
      /** Store token for service worker to authenticate transaction requests */
      case SecurityTypes.SECURITY_GET_ACCESS_INFO: {
        next(action);
        try {
          const databaseName = getDatabaseName(getState());
          const DB = await getDatabase(databaseName);
          const accessInfo = action.payload;
          DB.AccessToken.update(SYNC_ACCESS_TOKEN, {
            accessInfo,
            preferences: extractPreferencesFromAccessInfo(accessInfo),
          });
        } catch (error) {
          dispatch(writeLocalError(DB_TABLE_NAMES_MAP.AccessToken, error));
        }
        break;
      }
      case SecurityTypes.SECURITY_LOG_OUT: {
        const databaseName = getDatabaseName(getState());
        closeDb(databaseName);
        return next(action);
      }

      // If there was an API interruption, try to sync any transactions which were saved in the offline database.
      case ConnectionTypes.SET_SERVER_UP: {
        const state = getState();
        if (
          action.payload &&
          ConnectionSelectors.getOnline(state) &&
          token.isValid()
        ) {
          dispatch(syncOfflineTransactionHistory());
        }
        return next(action);
      }

      // If there was internet interruption, try to sync any transactions which were saved in the offline database.
      case ConnectionTypes.SET_ONLINE: {
        const state = getState();
        if (
          action.payload &&
          ConnectionSelectors.getServerUp(state) &&
          token.isValid()
        ) {
          dispatch(syncOfflineTransactionHistory());
        }
        return next(action);
      }

      case SetDateAndSiteTypes.GET_SERVING_SITES_FETCH: {
        try {
          const databaseName = getDatabaseName(getState());
          const DB = await getDatabase(databaseName);

          const servingSites = action.payload;
          await DB.ServingSites.bulkPut(servingSites);
        } catch (error) {
          dispatch(writeLocalError(DB_TABLE_NAMES_MAP.ServingSites, error));
        }
        return next(action);
      }

      case POSTypes.GET_POS_DATA_FETCH: {
        const {
          siteId,
          students,
          nutritionCodes,
          servedItems,
          sites,
          allergens,
          itemCategories,
          taxRatesByStatus,

          offline, // flag that detmermines that the data was retrieved from offline db
        } = action.payload;

        if (offline) {
          return next(action);
        }

        const databaseName = getDatabaseName(getState());
        const DB = await getDatabase(databaseName);

        try {
          await DB.ItemCategories.clear();
          await DB.ItemCategories.bulkPut(itemCategories);
        } catch (error) {
          dispatch(writeLocalError(DB_TABLE_NAMES_MAP.ItemCategories, error));
        }
        try {
          await DB.Allergens.clear();
          await DB.Allergens.bulkPut(allergens);
        } catch (error) {
          dispatch(writeLocalError(DB_TABLE_NAMES_MAP.Allergens, error));
        }
        try {
          await DB.Sites.clear();
          await DB.Sites.bulkPut(sites);
        } catch (error) {
          dispatch(writeLocalError(DB_TABLE_NAMES_MAP.Sites, error));
        }
        try {
          await DB.Students.clear();
          await DB.Students.bulkPut(students);
        } catch (error) {
          dispatch(writeLocalError(DB_TABLE_NAMES_MAP.Students, error));
        }
        try {
          await DB.NutritionCodes.clear();
          await DB.NutritionCodes.bulkPut(nutritionCodes);
        } catch (error) {
          dispatch(writeLocalError(DB_TABLE_NAMES_MAP.NutritionCodes, error));
        }
        try {
          await DB.ServedItems.where({ SiteId: siteId }).delete();
          await DB.ServedItems.bulkPut(servedItems);
        } catch (error) {
          dispatch(writeLocalError(DB_TABLE_NAMES_MAP.ServedItems, error));
        }
        try {
          await DB.TaxRatesByStatus.clear();
          await DB.TaxRatesByStatus.bulkPut(taxRatesByStatus);
        } catch (error) {
          dispatch(writeLocalError(DB_TABLE_NAMES_MAP.TaxRatesByStatus, error));
        }
        return next(action);
      }

      case POSTypes.WRITE_TRANSACTION_LOCAL: {
        try {
          const databaseName = getDatabaseName(getState());
          const DB = await getDatabase(databaseName);

          await DB.TransactionHistory.add(action.payload);
        } catch (error) {
          dispatch(
            writeLocalError(DB_TABLE_NAMES_MAP.TransactionHistory, error)
          );
        }
        return next(action);
      }

      case POSTypes.DELETE_TRANSACTION_LOCAL: {
        try {
          const databaseName = getDatabaseName(getState());
          const DB = await getDatabase(databaseName);

          const {
            PrimarySiteId,
            MealType,
            StudentIdSeed,
            TransactionType,
            WorkingDT,
          } = action.payload;

          await DB.TransactionHistory.where({
            PrimarySiteId,
            MealType,
            StudentIdSeed,
            [TransactionEnum.TransactionType]: getOfflineTransactionType(
              TransactionType
            ),
            WorkingDT,
          }).delete();
        } catch (error) {
          dispatch(
            writeLocalError(DB_TABLE_NAMES_MAP.TransactionHistory, error)
          );
        }
        return next(action);
      }

      case POSTypes.UPDATE_STUDENT: {
        try {
          const state = getState();
          const databaseName = getDatabaseName(state);
          const DB = await getDatabase(databaseName);
          const updateStudent = action.payload.update;

          const foundStudents = await DB.Students.where('IdSeed')
            .equals(action.payload.idSeed)
            .toArray();

          const newStudent = {
            AllergenString: updateStudent.Allergens.join(),
            Allergens: updateStudent.Allergens,
            Id: updateStudent.Id,
            IdSeed: updateStudent.IdSeed,
            ServingNumber: updateStudent.ServingNum,
            FullName: `${updateStudent.FirstName} ${updateStudent.LastName}`,
            Message: updateStudent.Message,
            SiteId: updateStudent.PrimarySiteId,
            FirstName: updateStudent.FirstName,
            LastName: updateStudent.LastName,
            StatusCurrent: updateStudent.StatusCurrent,
            GradeId: updateStudent.GradeId,
            Teacher: updateStudent.Teacher,
            TotalRows:
              foundStudents.length > 0 ? foundStudents[0].TotalRows : null,
            Balance: updateStudent.Balance,
          };

          //uses put instead of update to account for visiting students who may not be loaded into the offline DB
          await DB.Students.put(newStudent);
        } catch (error) {
          dispatch(writeLocalError(DB_TABLE_NAMES_MAP.Students, error));
        } finally {
          return next(action);
        }
      }

      default:
        return next(action);
    }
  };
};



// WEBPACK FOOTER //
// ./src/offline/transactionMiddleware.js